import React from 'react'
import MultiSelect from '../../components/MultiSelect'
import Button from '../../components/Button'
import { AccommodationEvent } from '../../../services/accommodationEvents'

export interface FilterOptions {
  classes: string[]
  students: string[]
  teachers: string[]
  accommodations: string[]
  dateRange: {
    startDate: string
    endDate: string
  }
}

interface MultiFilterProps {
  accommodationEvents: AccommodationEvent[]
  filters: FilterOptions
  onFiltersChange: (filters: FilterOptions) => void
}

const MultiFilter: React.FC<MultiFilterProps> = ({
  accommodationEvents,
  filters,
  onFiltersChange,
}) => {
  // Extract unique classes from accommodation events
  const getUniqueClasses = () => {
    const classesMap = new Map()
    accommodationEvents.forEach((event) => {
      if (event.class && event.class._id && event.class.name) {
        classesMap.set(event.class._id, event.class.name)
      }
    })
    return Array.from(classesMap.entries()).map(([_id, name]) => ({
      _id,
      name,
    }))
  }

  // Extract unique students from accommodation events
  const getUniqueStudents = () => {
    const studentsMap = new Map()
    accommodationEvents.forEach((event) => {
      if (event.student && event.student._id) {
        const fullName = `${event.student.firstName} ${event.student.lastName}`
        studentsMap.set(event.student._id, fullName)
      }
    })
    return Array.from(studentsMap.entries()).map(([_id, name]) => ({
      _id,
      name,
    }))
  }

  // Extract unique accommodations from accommodation events
  const getUniqueAccommodations = () => {
    const accommodationsSet = new Set<string>()
    accommodationEvents.forEach((event) => {
      if (event.accommodation) {
        accommodationsSet.add(event.accommodation)
      }
    })
    return Array.from(accommodationsSet).map((accommodation) => ({
      _id: accommodation,
      name: accommodation,
    }))
  }

  // Extract unique teachers from accommodation events
  const getUniqueTeachers = () => {
    const teachersMap = new Map()
    accommodationEvents.forEach((event) => {
      if (event.teacher && event.teacher._id) {
        const fullName = `${event.teacher.firstName} ${event.teacher.lastName}`
        teachersMap.set(event.teacher._id, fullName)
      }
    })
    return Array.from(teachersMap.entries()).map(([_id, name]) => ({
      _id,
      name,
    }))
  }

  // Handle filter changes
  const handleClassesChange = (selectedIds: string[]) => {
    onFiltersChange({
      ...filters,
      classes: selectedIds,
    })
  }

  const handleStudentsChange = (selectedIds: string[]) => {
    onFiltersChange({
      ...filters,
      students: selectedIds,
    })
  }

  const handleTeachersChange = (selectedIds: string[]) => {
    onFiltersChange({
      ...filters,
      teachers: selectedIds,
    })
  }

  const handleAccommodationsChange = (selectedIds: string[]) => {
    onFiltersChange({
      ...filters,
      accommodations: selectedIds,
    })
  }

  // Handle date range changes
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    let processedStartDate = startDate
    let processedEndDate = endDate

    // If startDate is provided, set it to beginning of day (00:00:00) in local time
    if (startDate) {
      // Create date in local timezone by using the date parts
      const [year, month, day] = startDate.split('-').map(Number)
      const start = new Date(year, month - 1, day, 0, 0, 0, 0)
      processedStartDate = start.toISOString()
    }

    // If endDate is provided, set it to end of day (23:59:59.999) in local time
    if (endDate) {
      // Create date in local timezone by using the date parts
      const [year, month, day] = endDate.split('-').map(Number)
      const end = new Date(year, month - 1, day, 23, 59, 59, 999)
      processedEndDate = end.toISOString()
    }

    onFiltersChange({
      ...filters,
      dateRange: {
        startDate: processedStartDate,
        endDate: processedEndDate,
      },
    })
  }

  // Clear all filters
  const clearAllFilters = () => {
    onFiltersChange({
      classes: [],
      students: [],
      teachers: [],
      accommodations: [],
      dateRange: { startDate: '', endDate: '' },
    })
  }

  const hasActiveFilters =
    filters.classes.length > 0 ||
    filters.students.length > 0 ||
    filters.teachers.length > 0 ||
    filters.accommodations.length > 0 ||
    filters.dateRange.startDate ||
    filters.dateRange.endDate

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <Button onClick={clearAllFilters} variant="link">
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Classes Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Classes
          </label>
          <MultiSelect
            options={getUniqueClasses()}
            selectedValues={filters.classes}
            onChange={handleClassesChange}
            placeholder="Filter by classes..."
            className="w-full"
          />
        </div>

        {/* Teachers Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Teachers
          </label>
          <MultiSelect
            options={getUniqueTeachers()}
            selectedValues={filters.teachers}
            onChange={handleTeachersChange}
            placeholder="Filter by teachers..."
            className="w-full"
          />
        </div>

        {/* Students Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Students
          </label>
          <MultiSelect
            options={getUniqueStudents()}
            selectedValues={filters.students}
            onChange={handleStudentsChange}
            placeholder="Filter by students..."
            className="w-full"
          />
        </div>

        {/* Accommodations Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Accommodations
          </label>
          <MultiSelect
            options={getUniqueAccommodations()}
            selectedValues={filters.accommodations}
            onChange={handleAccommodationsChange}
            placeholder="Filter by accommodations..."
            className="w-full"
          />
        </div>
      </div>

      {/* Date Range Filter */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">From</label>
        <input
          type="date"
          value={
            filters.dateRange.startDate
              ? (() => {
                  const date = new Date(filters.dateRange.startDate)
                  const year = date.getFullYear()
                  const month = String(date.getMonth() + 1).padStart(2, '0')
                  const day = String(date.getDate()).padStart(2, '0')
                  return `${year}-${month}-${day}`
                })()
              : ''
          }
          onChange={(e) =>
            handleDateRangeChange(
              e.target.value,
              filters.dateRange.endDate
                ? (() => {
                    const date = new Date(filters.dateRange.endDate)
                    const year = date.getFullYear()
                    const month = String(date.getMonth() + 1).padStart(2, '0')
                    const day = String(date.getDate()).padStart(2, '0')
                    return `${year}-${month}-${day}`
                  })()
                : '',
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">To</label>
        <input
          type="date"
          value={
            filters.dateRange.endDate
              ? (() => {
                  const date = new Date(filters.dateRange.endDate)
                  const year = date.getFullYear()
                  const month = String(date.getMonth() + 1).padStart(2, '0')
                  const day = String(date.getDate()).padStart(2, '0')
                  return `${year}-${month}-${day}`
                })()
              : ''
          }
          onChange={(e) =>
            handleDateRangeChange(
              filters.dateRange.startDate
                ? (() => {
                    const date = new Date(filters.dateRange.startDate)
                    const year = date.getFullYear()
                    const month = String(date.getMonth() + 1).padStart(2, '0')
                    const day = String(date.getDate()).padStart(2, '0')
                    return `${year}-${month}-${day}`
                  })()
                : '',
              e.target.value,
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Active filters summary */}
      {hasActiveFilters && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">Active filters:</span>
          {filters.classes.length > 0 && (
            <span className="ml-2 text-blue-600">
              {filters.classes.length} class
              {filters.classes.length !== 1 ? 'es' : ''}
            </span>
          )}
          {filters.students.length > 0 && (
            <span className="ml-2 text-green-600">
              {filters.students.length} student
              {filters.students.length !== 1 ? 's' : ''}
            </span>
          )}
          {filters.teachers.length > 0 && (
            <span className="ml-2 text-indigo-600">
              {filters.teachers.length} teacher
              {filters.teachers.length !== 1 ? 's' : ''}
            </span>
          )}
          {filters.accommodations.length > 0 && (
            <span className="ml-2 text-purple-600">
              {filters.accommodations.length} accommodation
              {filters.accommodations.length !== 1 ? 's' : ''}
            </span>
          )}
          {(filters.dateRange.startDate || filters.dateRange.endDate) && (
            <span className="ml-2 text-orange-600">
              Date range:{' '}
              {filters.dateRange.startDate
                ? new Date(filters.dateRange.startDate).toLocaleDateString()
                : 'start'}{' '}
              to{' '}
              {filters.dateRange.endDate
                ? new Date(filters.dateRange.endDate).toLocaleDateString()
                : 'end'}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiFilter
